import { z } from 'zod';
/**
 * What is actually running.
 *
 * A version is only worth showing if it can be WRONG in a way somebody would
 * notice, and `package.json` cannot be: every app in this monorepo says
 * `0.1.0` and has since the first commit. The identity that changes when a
 * deployment changes is the commit it was built from, so that is what this
 * reports.
 *
 * **Absent is reported as absent, never guessed.** A build with no commit
 * stamped in says so. Inventing "0.1.0" or falling back to a package version
 * would produce a confident string that is the same before and after a deploy —
 * worse than no version at all, because somebody would trust it.
 */
export const buildInfoSchema = z.object({
    /**
     * A human-readable release identity that still resolves to a commit —
     * `git describe --tags --always --dirty`.
     *
     * `v1.4.0` on a tagged release; `v1.4.0-95-g0416eb2` ninety-five commits after
     * one, which reads as a version AND names the exact commit; `-dirty` appended
     * when the tree had uncommitted changes, so "I am running something I have not
     * committed" is visible rather than inferred.
     *
     * A bare commit is precise and unreadable — nobody can tell whether `0416eb2`
     * is ahead of `7bda2b6`. This is the field somebody quotes in a bug report;
     * `commit` below is what an engineer checks out.
     */
    release: z.string().nullable(),
    /** Short commit the build came from. Null when nothing was stamped in. */
    commit: z.string().nullable(),
    /** ISO instant the artefact was built. Null when unstamped. */
    builtAt: z.string().datetime().nullable(),
    /** Branch, when the build recorded one. Useful on a review deployment. */
    branch: z.string().nullable(),
    /**
     * True when the build carries no stamp at all — a developer machine, or a
     * pipeline that forgot to pass one. Said plainly rather than shown as blanks.
     */
    unstamped: z.boolean(),
});
export const versionResponseSchema = z.object({
    api: buildInfoSchema,
    /** The environment name, when the deployment declares one. */
    environment: z.string().nullable(),
});
//# sourceMappingURL=system.js.map